import manifestJson from './asset-manifest.json';
import type {
  CharacterAppearance,
  CharacterBaseType,
  CharacterGender,
} from '../types';

export type CharacterAnchorName =
  | 'canvas'
  | 'headTop'
  | 'face'
  | 'ears'
  | 'neck'
  | 'wrist'
  | 'hand'
  | 'torso';

export type CharacterLayerType =
  | 'body'
  | 'tone'
  | 'body_detail'
  | 'outfit'
  | 'hair'
  | 'eyes'
  | 'mouth'
  | 'headwear'
  | 'eyewear'
  | 'audio'
  | 'jewelry'
  | 'prop';

export type AssetLayer = {
  family: string;
  option_type: CharacterLayerType;
  option_value: string;
  layer_file: string;
  z_index: number;
  anchor: CharacterAnchorName;
  color?: string | null;
  visible?: boolean;
  missing?: boolean;
  builder?: boolean;
  runtime_device?: boolean;
  art_quality?: 'illustrated-fitted' | 'placeholder';
};

type Anchor = { x: number; y: number };
type GenderVariant = {
  scale_x: number;
  scale_y: number;
  translate_y: number;
};
type FamilyManifest = {
  rigged: boolean;
  anchors: Record<Exclude<CharacterAnchorName, 'canvas'>, Anchor>;
  gender_variants: Record<CharacterGender, GenderVariant>;
  gender_art_ready?: boolean;
  palettes?: Record<string, string>;
};

type CharacterAssetManifest = {
  version: number;
  sprite: string;
  view_box: string;
  z_order: Record<CharacterLayerType, number>;
  species_map: Record<string, string>;
  families: Record<string, FamilyManifest>;
  layers: AssetLayer[];
};

export const characterAssetManifest =
  manifestJson as unknown as CharacterAssetManifest;

export function characterFamilyKey(
  baseType: CharacterBaseType,
  appearance: CharacterAppearance,
): string {
  if (baseType === 'library_animal') {
    return (
      characterAssetManifest.species_map[appearance.species || 'Owl'] || 'owl'
    );
  }
  return baseType;
}

export function familyManifest(family: string): FamilyManifest {
  return (
    characterAssetManifest.families[family] ||
    characterAssetManifest.families.owl
  );
}

export function resolveLayer(
  family: string,
  optionType: CharacterLayerType,
  optionValue: string,
): AssetLayer | null {
  const exact = characterAssetManifest.layers.find(
    layer =>
      layer.family === family &&
      layer.option_type === optionType &&
      layer.option_value === optionValue,
  );
  if (exact) return exact;
  return (
    characterAssetManifest.layers.find(
      layer =>
        layer.family === '*' &&
        layer.option_type === optionType &&
        layer.option_value === optionValue,
    ) || null
  );
}


export function manifestOptions(
  family: string,
  optionType: CharacterLayerType,
): string[] {
  const values = characterAssetManifest.layers
    .filter(
      layer =>
        (layer.family === family || layer.family === '*') &&
        layer.option_type === optionType &&
        layer.builder !== false,
    )
    .map(layer => layer.option_value);

  const unique = Array.from(new Set(values));
  const preferred: Partial<Record<CharacterLayerType, string>> = {
    outfit: 'Default body',
    headwear: 'None',
    eyewear: 'None',
    audio: 'None',
    jewelry: 'None',
    prop: 'None',
    hair: 'Curly afro',
  };
  const first = preferred[optionType];

  return unique.sort((a, b) => {
    if (a === first) return -1;
    if (b === first) return 1;
    return a.localeCompare(b);
  });
}


export function runtimeDeviceOptions(family: string): string[] {
  return Array.from(
    new Set(
      characterAssetManifest.layers
        .filter(
          layer =>
            (layer.family === family || layer.family === '*') &&
            layer.option_type === 'prop' &&
            layer.runtime_device === true,
        )
        .map(layer => layer.option_value),
    ),
  ).sort((a, b) => {
    const order = ['Phone', 'Tablet', 'Laptop'];
    return order.indexOf(a) - order.indexOf(b);
  });
}

export function toneOptionsForFamily(family: string): string[] {
  return Object.keys(familyManifest(family).palettes || {});
}

export function genderOptionsForFamily(family: string): CharacterGender[] {
  const config = familyManifest(family);
  if (config.gender_art_ready === false) return [];
  return Object.keys(config.gender_variants) as CharacterGender[];
}

export function libraryAnimalSpeciesOptions(): string[] {
  return Object.keys(characterAssetManifest.species_map);
}

export function selectedLayers(
  baseType: CharacterBaseType,
  appearance: CharacterAppearance,
): AssetLayer[] {
  const family = characterFamilyKey(baseType, appearance);
  const config = familyManifest(family);
  const gender =
    config.gender_art_ready === false ? undefined : appearance.gender;
  const tone =
    appearance.skinTone ||
    appearance.furTone ||
    appearance.bodyTone ||
    'Natural';
  const selections: Array<[CharacterLayerType, string]> = [
    ['body', gender || 'Base'],
    ['tone', tone],
    ...(gender
      ? ([['body_detail', gender]] as Array<[CharacterLayerType, string]>)
      : []),
    ...(baseType === 'library_animal'
      ? []
      : ([['outfit', appearance.outfit || 'Default body']] as Array<
          [CharacterLayerType, string]
        >)),
    ...(appearance.hair && appearance.hair !== 'Bald'
      ? ([['hair', appearance.hair]] as Array<[CharacterLayerType, string]>)
      : []),
    ['headwear', appearance.headwear || 'None'],
    ['eyewear', appearance.eyewear || 'None'],
    ['audio', appearance.audio || 'None'],
    ['jewelry', appearance.jewelry || 'None'],
    ['prop', appearance.prop || 'None'],
  ];

  return selections
    .map(([type, value]) => resolveLayer(family, type, value))
    .filter((layer): layer is AssetLayer => Boolean(layer))
    .filter(layer => layer.visible !== false)
    .sort((a, b) => a.z_index - b.z_index);
}

export function layerTransform(
  family: string,
  layer: AssetLayer,
  gender?: CharacterGender,
): string | undefined {
  const config = familyManifest(family);
  if (layer.anchor === 'canvas') {
    const genderScaled =
      layer.option_type === 'body' ||
      layer.option_type === 'tone' ||
      layer.option_type === 'body_detail' ||
      (family === 'owl' && layer.option_type === 'outfit');
    if (!genderScaled) return undefined;
    if (!gender) return undefined;
    const variant = config.gender_variants[gender];
    if (!variant) return undefined;
    return [
      'translate(160 ' + variant.translate_y + ')',
      'scale(' + variant.scale_x + ' ' + variant.scale_y + ')',
      'translate(-160 0)',
    ].join(' ');
  }

  const anchor = config.anchors[layer.anchor];
  return anchor ? 'translate(' + anchor.x + ' ' + anchor.y + ')' : undefined;
}

export function isFamilyRigged(family: string): boolean {
  return familyManifest(family).rigged === true;
}

export function preloadCharacterAssets(): void {
  if (typeof window === 'undefined') return;
  const image = new Image();
  image.src = characterAssetManifest.sprite;
}
